import type { SegmentAbstraction } from '@cc-shopping/platform-analytics/lib/Segment';
import { datadogRum } from '@datadog/browser-rum';
import type { ABSmartlyEventLogger, ExposureData } from '../types/absmartly';
import { isStaticExperiment } from '.';
import { logABSmartlyBucketingDecision } from './logBucketingDecision';
import { type ABTestVariantResult, getVariantsArray } from './variants';

type EventLoggerOpts = {
  analytics: SegmentAbstraction;
  anonymousId: string | null;
  deviceId: string;
  deployableVersion: string;
  gitSha: string;
  staticExperimentResult: ABTestVariantResult;
};

export const getABSmartlyEventLogger = ({
  analytics,
  anonymousId,
  deviceId,
  deployableVersion,
  gitSha,
  staticExperimentResult,
}: EventLoggerOpts) => {
  const absmartlyEventLogger: ABSmartlyEventLogger = (context, eventName, data = {}) => {
    if (eventName === 'error') {
      let errorData = data;

      if (!(errorData instanceof Error)) {
        errorData = new Error(`Unknown ABSmartly Error ${JSON.stringify(errorData)}`);
      }

      datadogRum.addError(errorData, {
        eventName,
        deployableVersion,
        source: 'ABSmartly',
        message: 'ABSmartly error',
        rawData: errorData,
      });
    }

    if (!(eventName === 'publish' && 'exposures' in data)) {
      return;
    }

    const exposures = data.exposures ?? [];
    const exposureEvents = exposures.reduce((acc: ExposureData[], exposure) => {
      const { name: experimentId, variant: variantIndex } = exposure;
      const defaultEmptyValue = '';
      const variants = getVariantsArray(context, experimentId, defaultEmptyValue);
      const isStatic = isStaticExperiment(experimentId);
      const variationKey = isStatic ? staticExperimentResult : variants[variantIndex];
      const shouldSkipExposure = variationKey === defaultEmptyValue || exposure.audienceMismatch;

      if (shouldSkipExposure) {
        return [...acc];
      } else {
        return [
          ...acc,
          {
            variationKey,
            experimentKey: experimentId,
            unitType: exposure.unit,
          },
        ];
      }
    }, []);

    // Log the exposure data to Segment
    logABSmartlyBucketingDecision(
      {
        deployableVersion,
        anonymousId,
        deviceId,
        attributes: {
          page_url: window?.location?.href ?? '',
        },
        exposureData: exposureEvents,
        gitSha,
      },
      analytics,
    );
  };

  return absmartlyEventLogger;
};
